/**
 * Categories — variant-agnostic engine.
 *
 * State machine for a Jeopardy-style single round:
 *  hub → board → clue → wager (daily double only) → answer → reveal → board → ... → roundEnd
 *
 * Owns:
 *  - daily-seeded board generation (6 categories × 5 dollar rungs = 30 clues)
 *  - tile selection / reveal state
 *  - Daily Double assignment, wager state
 *  - score accumulation
 *  - answer matching via answer-matcher.ts
 *
 * This file is variant-agnostic: no JSX, no CSS, no audio. Variants consume
 * `useBoardEngine` and render against the returned state.
 *
 * Board generation strategy:
 *  - Seed = mulberry32(YYYYMMDD hash) for "Today's Board"; random seed for Practice.
 *  - Pick 6 categories from the configured CATEGORY_ORDER.
 *  - For each category, shuffle its question pool with the seed and pick 5
 *    questions, mapping them to dollar rungs ($200/$400/$600/$800/$1000).
 *  - Rewrite each question's prompt into a Jeopardy "this/these" stem,
 *    keeping the correct option as the canonical answer ("What is X?").
 *  - Hide 1 Daily Double in a random non-$200 cell.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { DifficultyEngine, GameSession } from '@rendever/games-sdk';

import { matchesAnswer } from './answer-matcher';
import {
  buildDailyBoard,
  buildPracticeBoard,
  type BoardCategorySlug,
  type BoardClue,
  type BoardData,
  CATEGORY_LABELS,
} from './boards';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type BoardPhase =
  /** Hub (or initial mount). */
  | 'hub'
  /** Board grid is showing; player picks a tile. */
  | 'board'
  /** Clue text is on screen; player is thinking. */
  | 'clue'
  /** Daily Double wager UI is up; player setting their stake. */
  | 'wager'
  /** Answer input is up; player typing a guess. */
  | 'answer'
  /** Result of the answer is being shown. */
  | 'reveal'
  /** Round complete summary. */
  | 'roundEnd';

export type BoardMode = 'daily' | 'practice';

export type DollarValue = 200 | 400 | 600 | 800 | 1000;

export const DOLLAR_VALUES: ReadonlyArray<DollarValue> = [200, 400, 600, 800, 1000];

export interface BoardTileState {
  /** Stable id `${categorySlug}:${value}`. */
  id: string;
  category: BoardCategorySlug;
  value: DollarValue;
  /** True if this tile is the Daily Double. */
  dailyDouble: boolean;
  /** Has the player revealed this tile? */
  revealed: boolean;
  /** Did the player ultimately get this clue correct? (undefined until revealed) */
  correct?: boolean;
  /** The clue payload — stem + canonical answer. */
  clue: BoardClue;
}

export interface BoardEngineConfig {
  session: GameSession;
  difficulty: DifficultyEngine;
  mode: BoardMode;
  /** Optional override for the seed — used in tests and demo mode. */
  seedOverride?: number;
}

export interface BoardEngineState {
  phase: BoardPhase;
  mode: BoardMode;
  /** All 30 tiles in row-major order: row = dollar-rung index 0..4, col = category 0..5. */
  tiles: ReadonlyArray<BoardTileState>;
  /** The 6 category labels for this board (display order). */
  categoryLabels: ReadonlyArray<string>;
  /** Active tile index when phase is clue/wager/answer/reveal; null otherwise. */
  activeTileIndex: number | null;
  /** Player's running score (can go negative — but won't in v1, see policy below). */
  score: number;
  /** Daily Double wager (only valid while phase is wager/answer/reveal for a DD tile). */
  wager: number;
  /** True after the player submits an answer; the reveal phase is showing result. */
  lastAnswerCorrect: boolean | null;
  /** Most recently submitted raw text — kept for the reveal UI. */
  lastAnswerText: string;
  /** Number of tiles still unrevealed. */
  tilesRemaining: number;
  /** Score that was earned in this single answer (for celebrate animation). */
  lastDelta: number;
}

export interface BoardEngineActions {
  /** Begin a fresh board for the current mode. */
  start: () => void;
  /** Player picks a tile to reveal. Goes to clue / wager phase. */
  selectTile: (index: number) => void;
  /** Skip the clue display and move to answer input. */
  proceedToAnswer: () => void;
  /** Daily double — set the wager amount and proceed to clue. */
  setWager: (amount: number) => void;
  /** Submit the player's typed answer. */
  submitAnswer: (text: string) => void;
  /** Player declines to answer (or runs out of patience) — counts as incorrect. */
  passClue: () => void;
  /** Dismiss the reveal and return to the board. */
  dismissReveal: () => void;
  /** Restart in the current mode, or explicitly switch the next board's mode. */
  playAgain: (modeOverride?: BoardMode) => void;
  /** Go back to hub. */
  backToHub: () => void;
  /** End the session (telemetry flush). */
  end: () => Promise<void>;
  /** Dev hook — instantly reveal everything as correct (for screenshots). */
  devRevealAll: () => void;
  /** Dev hook — reveal N tiles at specified indices as correct. */
  devRevealSome: (indices: ReadonlyArray<number>) => void;
}

export interface BoardEngineHandle {
  state: BoardEngineState;
  actions: BoardEngineActions;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Today's date as YYYYMMDD in the local timezone — the daily seed input. */
export function todayDateKey(now: Date = new Date()): string {
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yyyy}${mm}${dd}`;
}

/** Cheap FNV-1a string hash → 32-bit unsigned. */
export function hashSeed(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function dailySeed(now: Date = new Date()): number {
  return hashSeed(`categories:${todayDateKey(now)}`);
}

/** The standard 6-row × 6-col layout. Row 0 is highest value (top), row 4 lowest. */
export function tileIndex(categoryCol: number, valueRow: number): number {
  return valueRow * 6 + categoryCol;
}

function tilesFromBoard(board: BoardData): BoardTileState[] {
  const tiles: BoardTileState[] = [];
  for (let row = 0; row < 5; row += 1) {
    for (let col = 0; col < 6; col += 1) {
      const clue = board.clues[col]![row]!;
      const value = DOLLAR_VALUES[row]!;
      tiles.push({
        id: `${board.categories[col]}:${value}`,
        category: board.categories[col]!,
        value,
        dailyDouble: col === board.dailyDoubleCol && row === board.dailyDoubleRow,
        revealed: false,
        clue,
      });
    }
  }
  return tiles;
}

// ---------------------------------------------------------------------------
// The hook
// ---------------------------------------------------------------------------

export function useBoardEngine(config: BoardEngineConfig): BoardEngineHandle {
  const { session, difficulty, mode, seedOverride } = config;

  /** Internal: which seed was used to construct the current board. */
  const seedRef = useRef<number>(0);

  const [phase, setPhase] = useState<BoardPhase>('board');
  const [tiles, setTiles] = useState<BoardTileState[]>(() => {
    const seed =
      seedOverride ?? (mode === 'daily' ? dailySeed() : Math.floor(Math.random() * 0xffff_ffff));
    seedRef.current = seed;
    const board = mode === 'daily' ? buildDailyBoard(seed) : buildPracticeBoard(seed);
    return tilesFromBoard(board);
  });
  const [categoryLabels, setCategoryLabels] = useState<string[]>(() =>
    tiles.slice(0, 6).map((t) => CATEGORY_LABELS[t.category]),
  );
  const [activeTileIndex, setActiveTileIndex] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const [wager, setWager] = useState(0);
  const [lastAnswerCorrect, setLastAnswerCorrect] = useState<boolean | null>(null);
  const [lastAnswerText, setLastAnswerText] = useState('');
  const [lastDelta, setLastDelta] = useState(0);

  const startedRef = useRef(false);
  const difficultyLoggedRef = useRef(false);

  // Keep categoryLabels in sync with tiles.
  useEffect(() => {
    setCategoryLabels(tiles.slice(0, 6).map((t) => CATEGORY_LABELS[t.category]));
  }, [tiles]);

  const tilesRemaining = useMemo(() => tiles.filter((t) => !t.revealed).length, [tiles]);

  // Auto-transition to roundEnd when all tiles revealed.
  useEffect(() => {
    if (phase === 'board' && tilesRemaining === 0 && startedRef.current) {
      setPhase('roundEnd');
      void session.emit('round_complete', { score, mode });
    }
  }, [phase, tilesRemaining, score, mode, session]);

  // Difficulty snapshot on first start.
  useEffect(() => {
    if (difficultyLoggedRef.current) return;
    if (!startedRef.current) return;
    difficultyLoggedRef.current = true;
    const snap = difficulty.snapshot();
    void session.emit('difficulty_snapshot', {
      axes: snap.axes,
      successRate: snap.successRate,
      resultCount: snap.resultCount,
    });
  }, [phase, difficulty, session]);

  const start = useCallback((): void => {
    if (!startedRef.current) {
      startedRef.current = true;
      void session.start();
    }
    setPhase('board');
  }, [session]);

  const selectTile = useCallback(
    (index: number): void => {
      if (phase !== 'board') return;
      const tile = tiles[index];
      if (!tile || tile.revealed) return;
      setActiveTileIndex(index);
      void session.emit('prompt_shown', {
        tileId: tile.id,
        category: tile.category,
        value: tile.value,
        dailyDouble: tile.dailyDouble,
      });
      if (tile.dailyDouble) {
        // Default wager = current score, floored at the dollar value of the tile.
        const minWager = tile.value;
        const maxWager = Math.max(minWager, score);
        setWager(Math.max(minWager, Math.min(maxWager, score || minWager)));
        setPhase('wager');
      } else {
        setPhase('clue');
      }
    },
    [phase, tiles, score, session],
  );

  const proceedToAnswer = useCallback((): void => {
    if (phase === 'wager') {
      // After locking in a wager, show the clue first.
      setPhase('clue');
      return;
    }
    if (phase === 'clue') {
      setPhase('answer');
      return;
    }
  }, [phase]);

  const setWagerAction = useCallback((amount: number): void => {
    setWager(amount);
  }, []);

  const submitAnswer = useCallback(
    (text: string): void => {
      if (phase !== 'answer' && phase !== 'clue') return;
      const idx = activeTileIndex;
      if (idx === null) return;
      const tile = tiles[idx];
      if (!tile) return;

      const isCorrect = matchesAnswer(text, tile.clue.canonicalAnswer, tile.clue.acceptableAnswers);
      const stake = tile.dailyDouble ? wager : tile.value;
      // v1 policy: no penalty for wrong answers (lose-on-wrong is brutal for this audience).
      // Daily Double behaves the same — wagers don't subtract.
      const delta = isCorrect ? stake : 0;

      setLastAnswerCorrect(isCorrect);
      setLastAnswerText(text);
      setLastDelta(delta);
      setScore((s) => s + delta);
      setTiles((prev) => {
        const next = prev.slice();
        next[idx] = { ...tile, revealed: true, correct: isCorrect };
        return next;
      });
      setPhase('reveal');

      void session.emit(isCorrect ? 'correct' : 'incorrect', {
        tileId: tile.id,
        category: tile.category,
        value: tile.value,
        dailyDouble: tile.dailyDouble,
        wager: tile.dailyDouble ? wager : tile.value,
        delta,
      });
    },
    [phase, activeTileIndex, tiles, wager, session],
  );

  const passClue = useCallback((): void => {
    if (phase !== 'clue' && phase !== 'answer' && phase !== 'wager') return;
    const idx = activeTileIndex;
    if (idx === null) return;
    const tile = tiles[idx];
    if (!tile) return;
    setLastAnswerCorrect(false);
    setLastAnswerText('');
    setLastDelta(0);
    setTiles((prev) => {
      const next = prev.slice();
      next[idx] = { ...tile, revealed: true, correct: false };
      return next;
    });
    setPhase('reveal');
    void session.emit('incorrect', {
      tileId: tile.id,
      category: tile.category,
      value: tile.value,
      passed: true,
    });
  }, [phase, activeTileIndex, tiles, session]);

  const dismissReveal = useCallback((): void => {
    setActiveTileIndex(null);
    setWager(0);
    setLastAnswerCorrect(null);
    setLastAnswerText('');
    setLastDelta(0);
    setPhase('board');
  }, []);

  const playAgain = useCallback(
    (modeOverride?: BoardMode): void => {
      const nextMode = modeOverride ?? mode;
      const seed = nextMode === 'daily' ? dailySeed() : Math.floor(Math.random() * 0xffff_ffff);
      seedRef.current = seed;
      const board = nextMode === 'daily' ? buildDailyBoard(seed) : buildPracticeBoard(seed);
      setTiles(tilesFromBoard(board));
      setActiveTileIndex(null);
      setScore(0);
      setWager(0);
      setLastAnswerCorrect(null);
      setLastAnswerText('');
      setLastDelta(0);
      setPhase('board');
    },
    [mode],
  );

  const backToHub = useCallback((): void => {
    setPhase('hub');
  }, []);

  const end = useCallback(async (): Promise<void> => {
    const totalAnswered = tiles.filter((t) => t.revealed).length;
    const totalCorrect = tiles.filter((t) => t.correct === true).length;
    const accuracy = totalAnswered > 0 ? totalCorrect / totalAnswered : 0;
    await session.end({
      finalScore: score,
      accuracy,
      completed: tilesRemaining === 0,
    });
  }, [tiles, tilesRemaining, score, session]);

  const devRevealAll = useCallback((): void => {
    setTiles((prev) =>
      prev.map((t) => ({
        ...t,
        revealed: true,
        correct: true,
      })),
    );
    setScore(
      tiles.reduce((acc, t) => acc + (t.dailyDouble ? Math.max(t.value, 1000) : t.value), 0),
    );
  }, [tiles]);

  const devRevealSome = useCallback((indices: ReadonlyArray<number>): void => {
    setTiles((prev) => {
      const next = prev.slice();
      let scoreDelta = 0;
      for (const i of indices) {
        const tile = next[i];
        if (!tile || tile.revealed) continue;
        next[i] = { ...tile, revealed: true, correct: true };
        scoreDelta += tile.value;
      }
      setScore((s) => s + scoreDelta);
      return next;
    });
  }, []);

  const state: BoardEngineState = {
    phase,
    mode,
    tiles,
    categoryLabels,
    activeTileIndex,
    score,
    wager,
    lastAnswerCorrect,
    lastAnswerText,
    tilesRemaining,
    lastDelta,
  };

  const actions: BoardEngineActions = {
    start,
    selectTile,
    proceedToAnswer,
    setWager: setWagerAction,
    submitAnswer,
    passClue,
    dismissReveal,
    playAgain,
    backToHub,
    end,
    devRevealAll,
    devRevealSome,
  };

  // Dev / e2e hook.
  if (typeof window !== 'undefined' && import.meta.env.DEV) {
    (window as unknown as { __boardActions?: BoardEngineActions }).__boardActions = actions;
    (window as unknown as { __boardState?: BoardEngineState }).__boardState = state;
  }

  return { state, actions };
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

export function formatDollars(amount: number): string {
  const sign = amount < 0 ? '-' : '';
  return `${sign}$${Math.abs(amount).toLocaleString('en-US')}`;
}
